import React, { useLayoutEffect, useRef } from 'react';
import { RiPencilLine, RiBarChartFill, RiListCheck, RiArrowLeftRightLine, RiArrowRightUpLine } from '@remixicon/react';
import SegmentedToggle from '../../../components/SegmentedToggle';
import CardShell from '../../../components/CardShell';
import { Cell, Choix } from '../../../components/InlineCells';
import { libelleUnite } from '../offerUnits';
import {
  MOIS_12, sumNN, cumNN, versMensuel, serieFlux, regleSaisie, tarifMoyen, tarifsMois, realLigne, realOffre,
} from './model';
import { setForecast, setForecastMonth, setForecastRule, setRealiseCell, renameForecastLine, deleteForecastLine,
  addForecastLine } from '../../../api/salesOfferMonths';

/* LA VUE D'UNE OFFRE (décisions d'Olivier, 2026-10-21 et 2026-10-22 ; brief `WIP/briefs/previsions-a3.md`).

   DEUX CARTES (proto du 2026-10-22, `WIP/protos/previsions-deux-cartes.html`) : la SYNTHÈSE FINANCIÈRE de l'offre
   (CA cible, écart à la cible, chiffre d'affaires et facturation de l'offre, en valeurs mensuelles ou cumulées) et
   le DÉTAIL DES VENTES (les lignes, où l'on saisit le réalisé et les ventes). Le Total réalisé suit le dernier
   mois clos ; la cible ne fait plus de colonne.

   Deux rangées de contrôle avec la comptabilité (le CA réalisé et le facturé de l'offre), puis un bloc par
   ligne de planification, le détail par ligne étant facultatif et sans effet sur le total prévisionnel.
   Une ligne se replie en UNE rangée (son chiffre d'affaires) ; dépliée, elle montre ses inducteurs, son prix
   unitaire, sa facturation et son chiffre d'affaires. À gauche le RÉALISÉ des mois clos (on le tape, en
   mensuel ou en cumulé), à droite le PLANIFIÉ des mois à venir ; puis cinq totaux : planifié + réalisé =
   prévisionnel, report, cible. Dans le réalisé le tarif se CALCULE (facturé ÷ volume) ; dans le planifié on le
   fixe, et le dernier chiffre tapé (volume ou facturation) tient, l'autre s'en déduit. */

const MOIS = (p) => [p('Jan'), p('Fév'), p('Mar'), p('Avr'), p('Mai'), p('Juin'),
                     p('Juil'), p('Août'), p('Sep'), p('Oct'), p('Nov'), p('Déc')];

export default function OfferCard({ c, r }) {
  const { p, tid, year, canWrite, fmt, vue, realise, saisie, flux, acc, exp, setExp, appel, valeurs } = c;
  const racine = useRef(null);
  const an = String(year);
  const cumule = saisie === 'cumul';              // le réalisé qu'on tape dans le détail
  const cumuleS = valeurs === 'cumul';             // les valeurs lues dans la synthèse
  const NA = <span className="om-na">{p('n/a')}</span>;
  const noms = MOIS(p);
  const ouverts = vue.open.filter((i) => i < 12);
  const clos = MOIS_12.filter((i) => !ouverts.includes(i));
  const montant = r.mode === 'amount';
  const f = c.facteur(r);                       // volume affiché / volume stocké (heures ⇄ jours-hommes)
  const mVol = fmt.volume(f);
  const mFr = fmt.money;
  const lignes = r.lines.filter((l) => !l.realise_seul);
  const ro = realOffre(realise, r);
  const total = flux === 'tot';
  /* Le revenu se reconnaît le mois de la vente et se facture à la livraison : la facturation EST alors la valeur
     tapée. Sinon (tranches, périodique, à la commande) la valeur tapée se dit « ventes » et la facturation, qui
     s'en calcule, a sa propre rangée. */
  const factDirecte = ((r.terms || {}).billing || 'on_delivery') === 'on_delivery';
  const sansLigne = !r.has_lines;
  const cleL = (l) => l.line_id || `o:${r.offer_id}`;
  const ouverte = (l) => exp.has(cleL(l));

  /* ── Écritures ─────────────────────────────────────────────────────── */
  const ecrireReel = (kind, lineId, i, brut, mensuels, cum = cumule) => appel(setRealiseCell(tid, r.offer_id, year, i + 1,
    { kind, ...(lineId ? { line_id: lineId } : {}), value: versMensuel(brut, mensuels, i, cum) }));
  const ecrireMois = (l, s, i, x, enFrancs) => appel(setForecastMonth(tid, r.offer_id, year, i + 1,
    { flux: s.flux, value: x, line_id: l.line_id || undefined, ...(enFrancs ? { in_chf: true } : {}) }));
  const ecrireAn = (l, champ, x, enFrancs = false) => appel(setForecast(tid, r.offer_id, year,
    { field: champ, value: x, line_id: l.line_id || undefined, ...(enFrancs ? { in_chf: true } : {}) }));
  const poserRegle = (l, regle) => appel(setForecastRule(tid, r.offer_id,
    { rule: regle, flux: flux === 'bk' ? 'bk' : 'new', line_id: l.line_id || undefined, year }));
  const basculer = (l) => setExp((x) => {
    const n = new Set(acc ? [...x].filter((k) => !lignes.some((m) => cleL(m) === k)) : x);
    if (x.has(cleL(l))) n.delete(cleL(l)); else n.add(cleL(l));
    return n;
  });
  const toutBasculer = () => setExp((x) => {
    const n = new Set(x);
    const ouvertes = lignes.some((l) => n.has(cleL(l)));
    lignes.forEach((l) => (ouvertes ? n.delete(cleL(l)) : n.add(cleL(l))));
    return n;
  });
  const ajouterLigne = () => appel(addForecastLine(tid, r.offer_id, p('Nouvelle ligne')));
  const supprimer = (l) => {
    if (window.confirm(p('Supprimer cette ligne et tout ce qu’elle porte, réalisé compris ?'))) appel(deleteForecastLine(tid, l.line_id));
  };
  const renommer = (l, label) => appel(renameForecastLine(tid, l.line_id, label));
  /* Le mode « rythme du réalisé » ne se propose que sur une ligne qui a du réalisé rattaché. */
  const aDuReel = (l) => {
    const rl = realLigne(realise, r, l);
    return Boolean(rl.ca) && clos.some((i) => rl.ca[i]);
  };

  /* ── Cellules ──────────────────────────────────────────────────────── */
  const premier = (i, liste) => i === liste[0];
  /* Le réalisé d'un mois clos : le total de l'offre (CA, facturé) se LIT, il se saisit dans Revenus réalisés ; le détail d'une ligne et le volume se tapent. */
  const reelTds = (arr, kind, lineId, mesure, editable = true, cum = cumule) => {
    const aff = cum ? cumNN(arr) : arr;
    return clos.map((i) => {
      const v = aff ? aff[i] : null;
      return (
        <td key={i} data-zone="reel" className={`lk-num pv-clos${premier(i, clos) ? ' lk-sep' : ''}`}>
          <Cell value={mesure.aff(v)} type="number" canWrite={canWrite && editable && (kind === 'volume' || Boolean(lineId))} placeholder={p('n/a')}
                format={(x) => (x == null ? '' : mesure.fmt(x) || '–')} toDraft={mesure.draft}
                onSave={(x) => ecrireReel(kind, lineId, i, mesure.brute(x), arr, cum)} />
        </td>
      );
    });
  };
  const luTds = (vals, fmtF, cls = '') => clos.map((i) => (
    <td key={i} className={`lk-num pv-clos${premier(i, clos) ? ' lk-sep' : ''}${cls}`}>
      {vals && vals[i] != null ? fmtF(vals[i]) : NA}
    </td>
  ));
  const blancsClos = () => clos.map((i) => <td key={i} className={`pv-hat${premier(i, clos) ? ' lk-sep' : ''}`} />);
  /* Le planifié d'un mois à venir : bleu s'il a été tapé dans la grandeur de cette rangée, noir sinon. */
  const prevTds = (vals, forme, mesure, ecrire, editable) => ouverts.map((i) => {
    const tape = forme && forme[i] === (mesure === mVol ? 'qty' : 'chf');
    return (
      <td key={i} data-zone="prev" className={`lk-num pv-prev${premier(i, ouverts) ? ' lk-sep' : ''}${tape ? '' : ' om-der'}`}>
        <Cell value={mesure.aff(vals[i])} type="number" canWrite={canWrite && editable} placeholder="–"
              format={(x) => (x == null ? '' : mesure.fmt(x) || '–')} toDraft={mesure.draft}
              onSave={(x) => ecrire(i, mesure.brute(x))} />
      </td>
    );
  });
  const luPrev = (vals, fmtF, base = null) => {
    let acc = base;
    return ouverts.map((i) => {
      let v = vals[i];
      if (acc != null) { acc += v || 0; v = acc; }
      return <td key={i} className={`lk-num pv-prev om-der${premier(i, ouverts) ? ' lk-sep' : ''}`}>{v ? fmtF(v) : '–'}</td>;
    });
  };
  /* Une rangée : le réalisé des mois clos, le Total réalisé qui les suit, le planifié des mois à venir, puis
     Total prévisions, Total prévisionnel et report. */
  const rangee = (reel, totReel, prev, plan, prevu, report) => (
    <>
      {reel}
      <td className="lk-num om-fort">{totReel}</td>
      {prev}
      <td className="lk-num pv-prev">{plan}</td>
      <td className="lk-num lk-sep om-fort">{prevu}</td>
      <td className="lk-num om-der">{report}</td>
    </>
  );
  const videsClos = () => clos.map((i) => <td key={i} className={premier(i, clos) ? 'lk-sep' : ''} />);
  const videsPrev = () => ouverts.map((i) => <td key={i} className={`pv-prev${premier(i, ouverts) ? ' lk-sep' : ''}`} />);
  const sur = (arr, idx) => sumNN(arr, idx);
  const txt = (v, fmtF) => (v == null ? NA : fmtF(v));

  /* ── La synthèse : CA cible, écart, puis le chiffre d'affaires et la facturation de l'offre ──
     Les mois clos portent le RÉALISÉ qu'on tape (le contrôle avec la comptabilité, sur la même donnée que la
     section Réalisé), les mois à venir le planifié que les lignes composent, toujours en TOTAL (portefeuille +
     nouvelles ventes) ; « Valeurs » les lit en mensuel ou en cumulé. Le report ne vaut que pour le chiffre
     d'affaires. */
  const rangOffre = (kind, libelle, fort = false) => {
    const arr = kind === 'ca' ? ro.ca : ro.billing;
    const plan = r[kind] || [];
    const totR = sur(arr, clos);
    const totP = sur(plan, ouverts);
    return (
      <tr key={`ctl-${kind}`} className={`om-row pv-ctl${fort ? ' pv-ctl--fin' : ''}`}>
        <td className="om-lbl lk-stick" style={{ '--stick': '0px' }}>{libelle}</td>
        {rangee(reelTds(arr, kind, null, mFr, true, cumuleS), txt(totR, fmt.val),
                luPrev(plan, fmt.val, cumuleS ? (totR || 0) : null),
                fmt.val(totP), fmt.val((totR || 0) + (totP || 0)), kind === 'ca' ? fmt.val((r.carry || {}).ca ?? 0) : '')}
      </tr>
    );
  };
  const reelCAoffre = sur(ro.ca, clos);
  const planCAoffre = sur(r.ca, ouverts);
  const prevOffre = (reelCAoffre || 0) + (planCAoffre || 0);
  const cible = r.target ? r.target.value : null;
  const reste = cible ? cible - prevOffre : null;
  /* LA CIBLE ET L'ÉCART DANS L'ENTÊTE de la synthèse (demande d'Olivier, 2026-10-22). */
  const reperesCible = cible ? (
    <div className="pv-reperes">
      <span className="pv-repere">{p('CA cible')} <b>{fmt.val(cible)}</b></span>
      <span className="pv-repere">{p('Écart à la cible')} <b className={reste > 0 ? 'om-neg' : 'om-pos'}>{fmt.val(reste)}</b></span>
    </div>
  ) : null;

  /* ── Une ligne de planification ───────────────────────────────────── */
  const blocLigne = (l) => {
    const s = serieFlux(l, flux);
    const rl = realLigne(realise, r, l);
    const regle = regleSaisie(l, flux);
    const prix = (l.price || {})[an];
    const prixTape = (l.price_source || {})[an] === 'line';
    const planCA = sur(s.ca, ouverts);
    const reelCA = sur(rl.ca, clos);
    const report = total ? ((l.carry || {}).ca ?? 0) : null;
    const prevCA = total ? (reelCA || 0) + (planCA || 0) : null;
    const nomEditable = canWrite && l.line_id;
    const rangCA = (reelCells) => rangee(reelCells, txt(reelCA, fmt.val), luPrev(s.ca, fmt.val), txt(planCA, fmt.val),
                                          prevCA == null ? '' : fmt.val(prevCA), report == null ? '' : fmt.val(report));
    const ouvert = ouverte(l);
    const chevron = (
      <button type="button" className={`pv-chev${ouvert ? ' is-open' : ''}`} aria-expanded={ouvert}
              aria-label={ouvert ? p('Replier la ligne') : p('Déplier la ligne')} onClick={() => basculer(l)}>›</button>
    );
    const nomL = nomEditable ? <Cell value={l.label} canWrite onSave={(x) => x && renommer(l, x)} /> : <span>{l.label}</span>;
    const sel = c.ligneChoisie === cleL(l) ? ' is-selected' : '';
    if (!ouvert) {
      return (
        <tr key={cleL(l)} className={`om-row pv-l1${sel}`}>
          <td className="om-lbl lk-stick" style={{ '--stick': '0px' }}><span className="pv-nom">{chevron}{nomL}</span></td>
          {rangCA(luTds(rl.ca, fmt.val))}
        </tr>
      );
    }
    const rows = [];
    const regleCell = (
      <span className={`om-prof ${regle === 'manual' ? 'om-prof--manuel' : 'om-prof--regle'}`}>
        <Choix value={regle} canWrite={canWrite} onSave={(v) => poserRegle(l, v || 'linear')}
               options={[{ value: 'linear', label: p('linéaire') }, { value: 'manual', label: p('manuel') },
                         ...c.profils.map((x) => ({ value: `profile:${x.key}`, label: x.name })),
                         ...(flux !== 'bk' ? [{ value: 'rhythm', label: p('rythme du réalisé'), disabled: !aDuReel(l),
                                              title: aDuReel(l) ? undefined : p('Aucun réalisé rattaché à cette ligne') }] : [])]} />
      </span>
    );
    rows.push(
      <tr key={`${cleL(l)}-h`} className={`om-row pv-lh${sel}`}>
        <td className="om-lbl lk-stick" style={{ '--stick': '0px' }}>
          <span className="pv-nom">{chevron}{nomL}{regleCell}
            {nomEditable && <button type="button" className="lk-x lk-del om-del" aria-label={p('Supprimer')} onClick={() => supprimer(l)}>✕</button>}
          </span>
        </td>
        <td colSpan={16} className="pv-lh__fin" />
      </tr>,
    );
    const lab = (t) => <td className="om-lbl lk-stick pv-sub" style={{ '--stick': '0px' }}>{t}</td>;
    /* Les inducteurs (jours-hommes, heures, pièces) : seulement pour une offre à quantités. */
    const reelVol = rl.volume;
    const peutVolume = !montant && Boolean(l.line_id);
    const planQty = sur(s.qty, ouverts);
    const reelQty = sur(reelVol, clos);
    if (!montant) {
      const editTotal = canWrite && regle !== 'manual' && regle !== 'rhythm';
      rows.push(
        <tr key={`${cleL(l)}-q`} className="om-row pv-r">
          {lab(libelleUnite(p, c.uniteAff(r)))}
          {rangee(
            peutVolume ? reelTds(reelVol, 'volume', l.line_id, mVol) : luTds(null, mVol.fmt),
            txt(reelQty == null ? null : mVol.aff(reelQty), mVol.fmt),
            prevTds(s.qty, s.formes, mVol, (i, x) => ecrireMois(l, s, i, x, false), true),
            <Cell value={mVol.aff(planQty)} type="number" canWrite={editTotal} placeholder="–"
                  format={(x) => mVol.fmt(x) || '–'} toDraft={mVol.draft}
                  onSave={(x) => ecrireAn(l, s.champ, mVol.brute(x), false)} />,
            total ? mVol.fmt(mVol.aff((reelQty || 0) + (planQty || 0))) : '', '')}
        </tr>,
      );
      const tarifs = tarifsMois(rl.billing, reelVol, cumule);
      rows.push(
        <tr key={`${cleL(l)}-p`} className={`om-row pv-r${prixTape ? '' : ' pv-der'}`}>
          {lab(<>{p('prix unitaire')} <small className="om-px">{`${c.dev} / ${libelleUnite(p, c.uniteAff(r))}`}</small></>)}
          {rangee(
            luTds(tarifs, (x) => fmt.prix(x / f)),
            txt(tarifMoyen(rl.billing, reelVol) == null ? null : tarifMoyen(rl.billing, reelVol) / f, fmt.prix),
            luPrev(ouverts.reduce((a, i) => { a[i] = (prix || 0) / f; return a; }, Array(12).fill(0)), fmt.prix),
            <Cell value={prix == null ? null : fmt.prixAff(prix / f)} type="number" canWrite={canWrite} placeholder="–"
                  format={(x) => (x == null ? '' : fmt.prixLu(x))}
                  onSave={(x) => ecrireAn(l, 'price', x == null ? null : Math.round(fmt.prixBrut(x) * f * 100) / 100)} />,
            '', '')}
        </tr>,
      );
    }
    /* La facturation (ou, si elle se calcule autrement, les ventes) : la valeur tapée en francs. */
    const planVal = sur(s.val, ouverts);
    const reelFact = sur(rl.billing, clos);
    const editValTotal = canWrite && regle !== 'manual' && regle !== 'rhythm';
    rows.push(
      <tr key={`${cleL(l)}-f`} className="om-row pv-r">
        {lab(factDirecte ? p('facturation') : p('ventes'))}
        {rangee(
          factDirecte ? reelTds(rl.billing, 'billing', l.line_id || null, mFr) : blancsClos(),
          factDirecte ? txt(reelFact, fmt.val) : '',
          prevTds(s.val, s.formes, mFr, (i, x) => ecrireMois(l, s, i, x, !montant), true),
          <Cell value={planVal} type="number" canWrite={editValTotal} placeholder="–"
                format={(x) => mFr.fmt(x) || '–'} toDraft={mFr.draft}
                onSave={(x) => ecrireAn(l, s.champ, mFr.brute(x), !montant)} />,
          factDirecte && total ? fmt.val((reelFact || 0) + (planVal || 0)) : '', '')}
      </tr>,
    );
    if (!factDirecte) {
      const planBill = sur(s.bill, ouverts);
      rows.push(
        <tr key={`${cleL(l)}-b`} className="om-row pv-r">
          {lab(p('facturation'))}
          {rangee(reelTds(rl.billing, 'billing', l.line_id || null, mFr), txt(reelFact, fmt.val), luPrev(s.bill, fmt.val),
                  fmt.val(planBill), total ? fmt.val((reelFact || 0) + (planBill || 0)) : '', '')}
        </tr>,
      );
    }
    rows.push(
      <tr key={`${cleL(l)}-c`} className="om-row pv-r">
        {lab(p("chiffre d'affaires"))}
        {rangCA(reelTds(rl.ca, 'ca', l.line_id || null, mFr))}
      </tr>,
    );
    return rows;
  };

  /* ── Le non affecté (toujours là, le réalisé s'en déduit : offre − lignes), le total du détail ── */
  const naCA = ro.nonAffecte.ca
    || (ro.ca ? MOIS_12.map((i) => ro.ca[i] - lignes.reduce((a, l) => a + ((realLigne(realise, r, l).ca || [])[i] || 0), 0)) : null);
  const naFA = ro.nonAffecte.billing
    || (ro.billing ? MOIS_12.map((i) => ro.billing[i] - lignes.reduce((a, l) => a + ((realLigne(realise, r, l).billing || [])[i] || 0), 0)) : null);
  const naOuverte = exp.has('na');
  const basculerNA = () => setExp((x) => { const n = new Set(x); if (n.has('na')) n.delete('na'); else n.add('na'); return n; });
  const rangNA = (cle, libelle, arr, repli) => (
    <tr key={`na-${cle}`} className={`om-row pv-na ${repli ? 'pv-l1' : 'pv-r'}`}>
      <td className={`om-lbl lk-stick${repli ? '' : ' pv-sub'}`} style={{ '--stick': '0px' }}>
        {repli ? (
          <span className="pv-nom">
            <button type="button" className={`pv-chev${naOuverte ? ' is-open' : ''}`} aria-expanded={naOuverte}
                    aria-label={naOuverte ? p('Replier la ligne') : p('Déplier la ligne')} onClick={basculerNA}>›</button>
            {libelle}
          </span>
        ) : libelle}
      </td>
      {rangee(luTds(cumule ? cumNN(arr) : arr, fmt.val), txt(sur(arr, clos), fmt.val), videsPrev(), '', '', '')}
    </tr>
  );
  const rangTotal = (
    <tr key="tot" className="om-row om-tot">
      <td className="om-lbl lk-stick" style={{ '--stick': '0px' }}>{p('Total du détail affiché')}</td>
      {rangee(luTds(cumule ? cumNN(ro.ca) : ro.ca, fmt.val), txt(reelCAoffre, fmt.val), luPrev(r.ca, fmt.val),
              fmt.val(planCAoffre), fmt.val(prevOffre), fmt.val((r.carry || {}).ca ?? 0))}
    </tr>
  );

  const lignesOuvertes = lignes.some((l) => ouverte(l));
  /* Les bandes : Réalisé (mois clos et leur total), Prévisions (mois à venir et leur total), Totaux. */
  const bandes = (coin, lienReel, lienPrev) => (
    <tr className="om-bandes">
      <th className="pv-coin">{coin}</th>
      {clos.length > 0 && (
        <th className="lk-band lk-band--real" colSpan={clos.length + 1}>
          <span className="pv-bt">{p('Réalisé')}{lienReel}</span>
        </th>
      )}
      {ouverts.length > 0 && (
        <th className="lk-band lk-band--fore" colSpan={ouverts.length + 1}>
          <span className="pv-bt">{p('Prévisions')}</span>{lienPrev}
        </th>
      )}
      <th className="lk-band lk-band--tot" colSpan={2}><span className="pv-bt">{p('Totaux')}</span></th>
    </tr>
  );
  const larg = c.unit === 'kchf' ? { m: 60, t: 76 } : { m: 82, t: 92 };
  const colgroup = (
    <colgroup>
      <col style={{ width: c.unit === 'kchf' ? 332 : 316 }} />
      {MOIS_12.map((i) => <col key={i} style={{ width: larg.m }} />)}
      {[0, 1, 2, 3].map((i) => <col key={`t${i}`} style={{ width: larg.t }} />)}
    </colgroup>
  );
  /* La rangée des mois : Total réalisé suit le dernier mois clos, puis Total prévisions, Total prévisionnel, Report. */
  const mois = (premiereCellule, ctx) => (
    <tr onContextMenu={ctx}>
      {premiereCellule}
      {clos.map((i) => <th key={i} className={`pv-pass${i === clos[0] ? ' lk-sep' : ''}`}>{noms[i]}</th>)}
      <th className="om-th2 pv-pass">{p('Total')}<br />{p('réalisé')}</th>
      {ouverts.map((i) => <th key={i} className={`pv-prev${i === ouverts[0] ? ' lk-sep' : ''}`}>{noms[i]}</th>)}
      <th className="om-th2 pv-prev">{p('Total')}<br />{p('prévisions')}</th>
      <th className="om-th2 lk-sep om-th--fort">{p('Total')}<br />{p('prévisionnel')}</th>
      <th className="om-th2">{p('Report')}<br />{Number(year) + 1}</th>
    </tr>
  );

  /* ── Les commandes des deux entêtes ───────────────────────────────── */
  const dev = c.dev;
  const reglagesSynthese = (
    <div className="pv-reglages" data-jan>
      <span className="pv-lab">{p('Valeurs')}</span>
      <SegmentedToggle value={valeurs} onChange={c.setValeurs} ariaLabel={p('Valeurs')}
                       options={[{ value: 'mensuel', label: p('Mensuel') }, { value: 'cumul', label: p('Cumulé') }]} />
    </div>
  );
  const reglagesDetail = (
    <div className="pv-reglages" data-jan>
      <span className="pv-lab">{p('Saisie du réalisé')}</span>
      <SegmentedToggle value={saisie} onChange={c.setSaisie} ariaLabel={p('Saisie du réalisé')}
                       options={[{ value: 'mensuel', label: p('Mensuel') }, { value: 'cumul', label: p('Cumulé') }]} />
      <span className="pv-lab">{p('Saisie des ventes')}</span>
      <span className="pv-vw">
        <SegmentedToggle value={flux} onChange={c.setFlux} ariaLabel={p('Saisie des ventes')}
                         options={[{ value: 'tot', label: p('Total') }, { value: 'bk', label: p('Portefeuille') },
                                   { value: 'new', label: p('Nouvelles ventes') }]} />
        {total && <span className="pv-indice">{p("Les modifications s'imputent aux nouvelles ventes")}</span>}
      </span>
    </div>
  );
  const actionsDetail = (
    <div className="pv-fin">
      {canWrite && <button type="button" className="lk-btn lk-btn--quiet" aria-pressed={acc} onClick={c.basculerAcc}>{p('Une ligne à la fois')}</button>}
      {(lignesOuvertes || !acc) && lignes.length > 0 && (
        <button type="button" className="lk-btn lk-btn--quiet" onClick={toutBasculer}>{lignesOuvertes ? p('Tout replier') : p('Tout déplier')}</button>
      )}
      {canWrite && r.equation !== 'base_rate' && <><span className="pv-filet" /><button type="button" className="lk-btn lk-btn--quiet pv-plus" onClick={ajouterLigne}>＋ {p('Ligne')}</button></>}
    </div>
  );
  const titre = (Icone, texte) => <span className="pv-ttl"><Icone size={28} aria-hidden="true" />{texte}</span>;
  const coinUnite = c.temps(r) ? (
    <button type="button" className="pv-unite" onClick={() => c.basculerUnite(r)}
            title={p('Lire en heures ou en jours-hommes')}>
      {p('Volume en')} {c.uniteAff(r) === 'h' ? p('heures') : p('jours-hommes')}<RiArrowLeftRightLine aria-hidden="true" />
    </button>
  ) : null;
  const lienReel = c.onGoRealise ? (
    <button type="button" className="pv-lien pv-lien--d" onClick={c.onGoRealise}>{p('Ouvrir Revenus réalisés')}<RiArrowRightUpLine size={13} aria-hidden="true" /></button>
  ) : null;

  /* Les commandes de chaque entête démarrent au bord gauche de la colonne de janvier : la largeur du bloc titre
     est variable, on la mesure. */
  useLayoutEffect(() => {
    if (!racine.current) return;
    racine.current.querySelectorAll('.std-card').forEach((carte) => {
      const reg = carte.querySelector('[data-jan]');
      const jan = carte.querySelector('thead tr:last-child th:nth-child(2)');
      if (!reg || !jan) return;
      reg.style.marginLeft = '0px';
      const d = jan.getBoundingClientRect().left - reg.getBoundingClientRect().left;
      reg.style.marginLeft = `${Math.max(0, d)}px`;
    });
  });

  return (
    <div className="pv-cartes" ref={racine}>
      <CardShell title={titre(RiBarChartFill, p('Synthèse financière de l\'offre'))} subtitle={dev} flush
                 viewControls={reglagesSynthese} primaryAction={reperesCible} className="pv-carte pv-carte--syn">
        <div className="om-scroll lk-hscroll">
          <table className="lk-entry-table om-table pv-grille">
            {colgroup}
            <thead>
              {bandes(null, lienReel, null)}
              {mois(
                <th className="om-lbl lk-stick" style={{ '--stick': '0px' }}>
                  {r.name}
                  {canWrite && <button type="button" className="pv-pen" title={p("Modifier l'offre")} aria-label={p("Modifier l'offre")}
                                       onClick={() => c.ouvrirOffre(r)}><RiPencilLine size={14} aria-hidden="true" /></button>}
                </th>,
                (e) => c.ouvrirMenu(e, r),
              )}
            </thead>
            <tbody>
              {rangOffre('ca', p("Chiffre d'affaires de l'offre"))}
              {rangOffre('billing', p("Facturation de l'offre"), true)}
            </tbody>
          </table>
        </div>
      </CardShell>
      <CardShell title={titre(RiListCheck, p('Détail des ventes'))} subtitle={dev} flush
                 viewControls={reglagesDetail} primaryAction={actionsDetail} className="pv-carte pv-carte--det">
        <div className="om-scroll lk-hscroll">
          <table className="lk-entry-table om-table pv-grille">
            {colgroup}
            <thead>
              {bandes(coinUnite, null, null)}
              {mois(<th className="om-lbl lk-stick" style={{ '--stick': '0px' }} />, null)}
            </thead>
            <tbody>
              {lignes.map((l) => blocLigne(l))}
              {!sansLigne && clos.length > 0 && rangNA('ca', p('Non affecté'), naCA, true)}
              {!sansLigne && clos.length > 0 && naOuverte && rangNA('fa', p('facturation'), naFA, false)}
              {!sansLigne && clos.length > 0 && naOuverte && rangNA('ca2', p("chiffre d'affaires"), naCA, false)}
              {rangTotal}
            </tbody>
          </table>
        </div>
      </CardShell>
    </div>
  );
}
